import * as _ from 'underscore';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { SiteUrls } from '@tableau/shared-urls';

import { Formatters, Messages } from '../../../../../codegen/Localize';
import { showActionableToast } from '../../../../../common/react/components/ActionableMessage';
import { Location } from '../../../../../common/react/Location';
import { CollectionsErrorCode } from '../../../../../ts/helpers/ErrorCodes';
import { IContentInfo } from '../../../../../ts/models/IContentInfo';
import { IToastOptions, ToasterService, ToastType } from '../../../../../ts/services/ToasterService';
import {
  ChangeCollectionVisibility,
  CreateEmptyCollection,
  CreateNoNameCollection,
  OpenManageItemsInCollectionsDialog,
  RemoveFromThisCollection
} from '../../../../actions/AppActionTypes';
import { requestFailed } from '../../../../actions/RequestFailedAction';
import { ChangeCollectionVisibilityAction } from '../../../../actions/spaces/Collections/ChangeCollectionVisibilityAction';
import {
  ManageItemsInCollectionsDialogAction,
  openManageItemsInCollectionsDialogAction
} from '../../../../actions/spaces/Collections/ManageItemsInCollectionAction';
import { RemoveFromThisCollectionAction } from '../../../../actions/spaces/Collections/RemoveFromThisCollectionAction';
import {
  createEmptyCollectionAction,
  CreateEmptyCollectionAction,
  CreateNoNameCollectionAction
} from '../../../../actions/spaces/ContentExplorerCreateActions';
import { CompositeSessionInfoSelectors, SessionUserSelectors } from '../../../../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../../../selectors/SiteUrlsSelectors';
import { selectSpaceInfoItem } from '../../../../selectors/SpaceInfoSelectors';
import { ChangeCollectionVisibilityDialog } from '../../../../spaces/Collections/ChangeCollectionVisibilityDialog';
import { AddRemoveCollectionItemsData } from '../../../../spaces/Collections/CollectionModels';
import { CollectionItemsResourceQuery, CollectionsResourceQuery } from '../../../../spaces/Collections/Collections';
import { CreateCollectionDialog } from '../../../../spaces/Collections/CreateCollectionDialog';
import { ICollectionInfo } from '../../../../spaces/Collections/ICollectionInfo';
import { ManageItemsInCollectionsDialog } from '../../../../spaces/Collections/ManageItemsInCollectionsDialog';
import { reloadSpace } from '../RefreshContentSagas';
import { ExplorableItemType } from '../../../../../ts/models/ContentType';
import { deepClone } from '../../../../../ts/helpers/DeepClone';
import { IDataSourceInfo } from '../../../../../ts/models/IDataSourceInfo';

export function getItemsWithCorrectLuids(items: IContentInfo<ExplorableItemType>[]): IContentInfo<ExplorableItemType>[] {
  // Data Sources from CRASS/content exploration have two luids: datasourceLuid and luid. All REST APIs operate on datasourceLuid
  // so override the luid property with the datasourceLuid for data sources.
  // getDatasource(s) and exploreContent return only one luid for data sources (the one that REST APIs use) so don't need to
  // transform data sources from those APIs.
  const targetItemsWithCorrectLuids = items.map((item: IContentInfo<ExplorableItemType>) => {
    if (item.hasOwnProperty('datasourceLuid') && item.contentType === 'datasource') {
      return replaceLuidWithDataSourceLuid(item);
    } else {
      return item;
    }
  });

  return targetItemsWithCorrectLuids;
}

function replaceLuidWithDataSourceLuid(item: IContentInfo<ExplorableItemType>) {
  // override the luid with the datasource luid for using a consistent luid in collections
  let ds = deepClone(item);
  ds.luid = (item as IDataSourceInfo).datasourceLuid;
  return ds;
}


// Create Empty Collection- this is from the ManageItemsInCollections/CreateCollectionDialog
export function* handleCreateEmptyCollection(action: CreateEmptyCollectionAction) {
  try {
    const urls: SiteUrls = yield select(siteUrlsSelector);
    const addItemsToCollection: ICollectionInfo = yield call(CreateCollectionDialog.open, urls, action.itemsToPossiblyAdd);
    if (addItemsToCollection) {
      yield put(openManageItemsInCollectionsDialogAction(action.itemsToPossiblyAdd, [ addItemsToCollection.luid ]));
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create Empty Collection without a name-- this is for creating a collection from the
// Create button
export function* handleCreateNoNameCollection(action: CreateNoNameCollectionAction) {
  try {
    const urls = yield select(siteUrlsSelector);
    const resource = new CollectionsResourceQuery();
    const currentUrl = Location.instance.href();
    yield call(createCollectionAndShowToasts, resource, urls, currentUrl);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function createCollectionAndShowToasts(
  resource: CollectionsResourceQuery, urls: SiteUrls, currentUrl: string) {
  resource.create()
    .then((collectionInfo: ICollectionInfo) => {
      // navigate to the new collection
      Location.instance.setUrl(urls.collectionUrl(collectionInfo.luid));

      // show toast with undo *after* navigating to the collection
      _.defer(() =>
        showActionableToast({
          testId: 'collection-creation-success',
          message: Messages.createCollectionSuccess(),
          actionMessage: Messages.createCollectionUndo(),
          action: () => {
            resource.delete([collectionInfo.luid])
              .catch(() => {
                undoFailed();
              });
            Location.instance.setUrl(currentUrl);
          }
        },
          ToastType.Success)
      );
    })
    .catch((result: any) => {
      const errorCode = result && result.data && result.data.errorCode;
      if (errorCode === CollectionsErrorCode.DUPLICATE_KEY) {
        // If for some reason this failed and there was still a duplicate name, just try again
        createCollectionAndShowToasts(resource, urls, currentUrl);
      } else {
        const toaster = ToasterService.instance;
        const toastOps: IToastOptions = {
          type: ToastType.Error,
          text: Messages.errorUnknown(),
          testId: 'create-no-name-collection-error'
        };
        toaster.pop(toastOps);
      }
    });

    function undoFailed() {
        const toaster: ToasterService = ToasterService.instance;
        toaster.markToastsAsDoNotDismissOnLocationChange();
        toaster.pop({
            type: ToastType.Error,
            text: Messages.deleteCollectionsError({
                ERROR_COUNT: 1,
                ERROR_COUNT_LOC: Formatters.formatCount(1),
            })
        });
    }
}


// AddItemsDialog
export function* handleManageItemsInCollectionDialog(action: ManageItemsInCollectionsDialogAction) {
  try {
    const targetItemsWithCorrectLuids = getItemsWithCorrectLuids(action.targetItems);
    const collectionsCount = yield call(getCollectionsCount);

    if (collectionsCount < 1) {
      yield put(createEmptyCollectionAction(targetItemsWithCorrectLuids));
    } else {
      const userId = yield select(SessionUserSelectors.sessionUserId);
      const isAdmin = yield select(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);

      const dialogOptions: ManageItemsInCollectionsDialog.Options = {
        items: targetItemsWithCorrectLuids,
        targetCollectionLuids: action.targetCollectionLuids,
        userId,
        isAdmin,
      };

      yield call(ManageItemsInCollectionsDialog.open, dialogOptions);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getCollectionsCount() {
  return new CollectionsResourceQuery().getCount();
}

// Change Collection Visibility
export function* handleChangeCollectionVisibility(action: ChangeCollectionVisibilityAction) {
  try {
    const closeReason = yield call(ChangeCollectionVisibilityDialog.open, action.targetItem);
    if (closeReason === ChangeCollectionVisibilityDialog.RESULT_CONFIRM) {
      yield call(reloadSpace);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleRemoveFromThisCollection(action: RemoveFromThisCollectionAction) {
  try {
    const collection: ICollectionInfo = yield select(selectSpaceInfoItem);

    // do this everywhere for collection items
    const targetItemsWithCorrectLuids = getItemsWithCorrectLuids(action.targetItems);
    const response: AddRemoveCollectionItemsData = yield call(removeFromThisCollection, collection, targetItemsWithCorrectLuids);

    // there is a scenario where the number removed isn't equal to the requested
    // number. saving the higher value to use
    const numRemoved = Math.max(
      targetItemsWithCorrectLuids.length-response.errors.length,
        response.numItemsSuccessfullyUpdated);

    // there might be a case to do this regardless to avoid inconsistencies with data on the server
    if(numRemoved > 0) {
      yield call(reloadSpace);
    }

    // in the case of one successful removal request, we have all the info to display a more detailed toast
    if(targetItemsWithCorrectLuids.length === 1 && numRemoved === 1) {
      const message = Messages.removedSingleItemFromCollection({
        ITEM_NAME: targetItemsWithCorrectLuids[0].name,
        COLLECTION_NAME: collection.name
      });

      yield call(showRemoveItemToast, message, ToastType.Info);
    } else if(numRemoved > 0) {
      const message = Messages.removedMultipleItemsFromCollection({
        ITEM_COUNT: numRemoved,
        ITEM_COUNT_LOC: Formatters.formatCount(numRemoved),
        COLLECTION_NAME: collection.name
      });

      yield call(showRemoveItemToast, message, ToastType.Info);
    }

    if(response.errors.length > 0) {
      const message = Messages.removeItemsFromCollectionErrors({
        ITEM_COUNT: response.errors.length,
        ITEM_COUNT_LOC: Formatters.formatCount(response.errors.length),
        COLLECTION_NAME: collection.name
      });

      yield call(showRemoveItemToast, message, ToastType.Error);
    }

  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function showRemoveItemToast(message: string, toastType: ToastType) {
  const toaster = ToasterService.instance;
  const toastOps: IToastOptions = {
    type: toastType,
    text: message,
    testId: 'removeItemToast'
  };
  toaster.pop(toastOps);
}

export function removeFromThisCollection(collection: ICollectionInfo, items: IContentInfo[]) {
  const resource = new CollectionItemsResourceQuery(collection.luid);
  return resource.removeItemsFromCollection(items);
}

export function* rootCollectionSaga(): SagaIterator {
  yield takeLatest(ChangeCollectionVisibility, handleChangeCollectionVisibility);
  yield takeLatest(CreateEmptyCollection, handleCreateEmptyCollection);
  yield takeLatest(OpenManageItemsInCollectionsDialog, handleManageItemsInCollectionDialog);
  yield takeLatest(CreateNoNameCollection, handleCreateNoNameCollection);
  yield takeLatest(RemoveFromThisCollection, handleRemoveFromThisCollection);
}
